import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { connectCalendarProvider, runCalendarMonitor, syncCalendar, backfillServiceRequests, updateCalendarConfig } from "../api/calendarApi";

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
];

/** TASK-522 / 541 / 536 — owner provider picker; Google live OAuth; shop-mgmt stubs. */
export default function CalendarConnectPanel({
  shopId,
  config,
  connection,
  providers,
  onSaved,
  embedded = false,
}) {
  const [providerType, setProviderType] = useState(
    config?.provider_type === "none" ? "" : config?.provider_type || "",
  );
  const [timezone, setTimezone] = useState(config?.timezone || "America/New_York");
  const [bufferMinutes, setBufferMinutes] = useState(config?.buffer_minutes ?? 15);
  const [slotMinutes, setSlotMinutes] = useState(config?.slot_duration_minutes ?? 60);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setProviderType(config?.provider_type === "none" ? "" : config?.provider_type || "");
    setTimezone(config?.timezone || "America/New_York");
    setBufferMinutes(config?.buffer_minutes ?? 15);
    setSlotMinutes(config?.slot_duration_minutes ?? 60);
  }, [config, connection]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const google = params.get("google");
    if (google === "connected") {
      setMessage("Google Calendar connected.");
      onSaved?.();
    } else if (google === "error") {
      setError(`Google connect failed: ${params.get("reason") || "unknown"}`);
    }
  }, [onSaved]);

  const handleSaveConfig = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await updateCalendarConfig(shopId, {
        provider_type: providerType || "none",
        timezone,
        buffer_minutes: Number(bufferMinutes),
        slot_duration_minutes: Number(slotMinutes),
      });
      setMessage("Calendar settings saved.");
      onSaved?.();
    } catch (err) {
      setError(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleConnectProvider = async () => {
    if (!providerType) {
      setError("Choose a calendar provider first.");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const result = await connectCalendarProvider(shopId, {
        provider_type: providerType,
      });
      if (result.authUrl) {
        window.location.assign(result.authUrl);
        return;
      }
      setMessage(result.message || "Provider saved.");
      onSaved?.();
    } catch (err) {
      setError(err.message || "Failed to connect provider");
    } finally {
      setSaving(false);
    }
  };

  const handleSyncNow = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const result = await syncCalendar(shopId);
      if (result.skipped) {
        setMessage(result.message || "Nothing to sync — connect a provider first.");
      } else if (result.message && result.upserted === 0 && result.pushed === 0) {
        setError(result.message);
      } else {
        setMessage(
          `Synced. Pushed ${result.pushed ?? 0} to Google; Google listed ${result.listed ?? 0}, updated ${result.upserted ?? 0}` +
            (result.deduped ? `, removed ${result.deduped} duplicate(s)` : "") +
            ".",
        );
        onSaved?.();
      }
    } catch (err) {
      setError(err.message || "Sync failed");
    } finally {
      setSaving(false);
    }
  };

  const handleCheckHealth = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const result = await runCalendarMonitor(shopId);
      if (result.health?.ok) {
        setMessage(
          `Connection healthy. Queue drain: ${result.drain?.completed ?? 0} completed, ${result.drain?.deferred ?? 0} deferred.`,
        );
      } else {
        setError(
          result.health?.message ||
            "Calendar connection is unhealthy — reconnect your provider.",
        );
      }
      onSaved?.();
    } catch (err) {
      setError(err.message || "Health check failed");
    } finally {
      setSaving(false);
    }
  };

  const handleBackfillServiceRequests = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const result = await backfillServiceRequests(shopId);
      setMessage(
        `Service requests synced. Eligible ${result.eligible ?? 0}: created ${result.created ?? 0}, updated ${result.updated ?? 0}, skipped ${result.skipped ?? 0}` +
          (result.failed ? `, failed ${result.failed}` : "") +
          ".",
      );
      onSaved?.();
    } catch (err) {
      setError(err.message || "Service request sync failed");
    } finally {
      setSaving(false);
    }
  };

  const selected = providers.find((p) => p.providerType === providerType);
  const isUnhealthy = connection?.connection_status === "unhealthy";
  const isConnected = connection?.connection_status === "connected";
  const connectLabel =
    selected?.liveConnect && providerType === "google_calendar"
      ? "Connect Google Calendar"
      : "Save provider choice";

  const content = (
    <>
      {!embedded && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect Google Calendar for two-way sync (when enabled). Shop management systems use the
          same calendar framing and will plug in as adapters later.
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {message}
        </Alert>
      )}
      {isUnhealthy && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Calendar sync is down
          {connection?.last_error ? `: ${connection.last_error}` : "."} You can still
          create appointments in Autoshops; reconnect Google to resume sync. Voice bookings
          will queue until the connection is healthy.
        </Alert>
      )}

      <Stack spacing={2}>
        <FormControl fullWidth size="small">
          <InputLabel id="calendar-provider-label">Calendar provider</InputLabel>
          <Select
            labelId="calendar-provider-label"
            label="Calendar provider"
            value={providerType}
            onChange={(e) => setProviderType(e.target.value)}
          >
            <MenuItem value="">
              <em>None — Autoshops calendar only</em>
            </MenuItem>
            {providers.map((p) => (
              <MenuItem key={p.providerType} value={p.providerType}>
                {p.displayName}
                {p.liveConnect ? " (live)" : " (coming soon)"}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="calendar-tz-label">Timezone</InputLabel>
            <Select
              labelId="calendar-tz-label"
              label="Timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip
            arrow
            title="Buffer is the padding between appointments. Example: 15 means leave 15 minutes before the next available booking slot."
          >
            <TextField
              size="small"
              label="Buffer (minutes)"
              type="number"
              value={bufferMinutes}
              onChange={(e) => setBufferMinutes(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: <HelpOutlineIcon fontSize="small" color="action" />,
              }}
            />
          </Tooltip>
          <Tooltip
            arrow
            title="Slot length is the default booking grid size for finding availability. It does not force every service to be 60 minutes; each appointment still has its own start and end time."
          >
            <TextField
              size="small"
              label="Slot length (minutes)"
              type="number"
              value={slotMinutes}
              onChange={(e) => setSlotMinutes(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: <HelpOutlineIcon fontSize="small" color="action" />,
              }}
            />
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="contained" disabled={saving} onClick={handleSaveConfig}>
            Save settings
          </Button>
          <Button
            variant="outlined"
            disabled={saving || !providerType}
            onClick={handleConnectProvider}
          >
            {connectLabel}
          </Button>
          {isConnected && (
            <Button variant="text" disabled={saving} onClick={handleSyncNow}>
              Sync now
            </Button>
          )}
          {(isConnected || isUnhealthy) && (
            <Button variant="text" disabled={saving} onClick={handleCheckHealth}>
              Check connection
            </Button>
          )}
          <Button variant="text" disabled={saving} onClick={handleBackfillServiceRequests}>
            Sync service requests
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary">
          Connection status: {connection?.connection_status || "disconnected"}
          {connection?.provider_type ? ` · ${connection.provider_type}` : ""}
        </Typography>
      </Stack>
    </>
  );

  if (embedded) {
    return <Box>{content}</Box>;
  }

  return (
    <Box sx={{ mb: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Calendar setup (shop owner)
      </Typography>
      {content}
    </Box>
  );
}
